import React, { useEffect, useMemo, useState } from "react";
import { TL } from "../i18n";
import client from "../lib/api";
import { ChevronDown, ChevronRight } from "lucide-react";
import SeederHost, { SEEDER_LABEL, slug, holdingValue } from "./SeederHost";
import { money } from "../lib/holdingsMath";

const BLOCKS = [
  { key: "PRIVATE", items: ["Private Debt", "Private Equity", "Private Realty", "Distressed Debt", "Globally Listed Private Equity ETF"] },
  { key: "MANAGED", items: ["Managed Portfolios", "Mutual Funds", "2nd Buyouts", "Carve-outs", "ETFs"] },
  { key: "VENTURE", items: ["Venture Capital", "Hedge Fund", "Sidecar (Life and Annuity)", "Sidecar (Property and Casualty)", "DarkPool/ATS"] },
];
const optKey = (block, item) => `altseed:${block}:${item}`;
const DEFAULT_SEEDERS = { "MANAGED|Mutual Funds": ["third_party"] }; // changeable default

export default function AltAssetsLedger({ items = [], reload }) {
  const [seeders, setSeeders] = useState({}); // "block|item" -> [seeder keys]
  const [openItem, setOpenItem] = useState(null);
  const [openBlocks, setOpenBlocks] = useState(() => new Set(["PRIVATE"]));

  const loadSeeders = () => client.get("/options/holdings").then((r) => {
    const map = {};
    Object.entries(r.data || {}).forEach(([k, v]) => {
      if (k.startsWith("altseed:")) {
        const rest = k.slice("altseed:".length);
        const idx = rest.indexOf(":");
        map[`${rest.slice(0, idx)}|${rest.slice(idx + 1)}`] = Array.isArray(v) ? v.filter(Boolean) : [];
      }
    });
    setSeeders(map);
  }).catch(() => {});
  useEffect(() => { loadSeeders(); }, []);

  const itemsFor = (block, item) => items.filter((h) => h.alt_block === block && h.alt_item === item);
  const valueFor = (block, item) => itemsFor(block, item).reduce((a, h) => a + holdingValue(h), 0);
  const loadedFor = (block, item) => { const k = `${block}|${item}`; return (k in seeders) ? seeders[k] : (DEFAULT_SEEDERS[k] || []); };
  const saveSeeders = async (block, item, arr) => {
    setSeeders((p) => ({ ...p, [`${block}|${item}`]: arr }));
    await client.put(`/options/holdings/${optKey(block, item)}`, { values: arr }).catch(() => {});
  };
  const addSeeder = (block, item, s) => { const arr = loadedFor(block, item); if (!arr.includes(s)) saveSeeders(block, item, [...arr, s]); };
  const removeSeeder = (block, item, s) => saveSeeders(block, item, loadedFor(block, item).filter((x) => x !== s));

  const toggleBlock = (b) => setOpenBlocks((prev) => { const n = new Set(prev); n.has(b) ? n.delete(b) : n.add(b); return n; });
  const totalRecords = useMemo(() => items.filter((h) => h.alt_block && h.alt_item).length, [items]);

  return (
    <div className="space-y-3" data-testid="alt-assets">
      <div className="text-[11px] opacity-60">{TL("Three fixed blocks. Each item can run")} <b>{TL("multiple seeders")}</b> at once; records stay scoped to Alternative Assets. {totalRecords} record(s) across all items.</div>
      {BLOCKS.map((blk) => {
        const isOpen = openBlocks.has(blk.key);
        const blkCount = blk.items.reduce((a, it) => a + itemsFor(blk.key, it).length, 0);
        const blkValue = blk.items.reduce((a, it) => a + valueFor(blk.key, it), 0);
        return (
          <div key={blk.key} className="rounded-lg border border-black/10 dark:border-white/10" data-testid={`aa-block-${blk.key}`}>
            <button type="button" onClick={() => toggleBlock(blk.key)} data-testid={`aa-block-toggle-${blk.key}`}
              className="flex w-full items-center gap-2 bg-black/[0.04] px-3 py-2 text-start hover:bg-black/[0.07] dark:bg-white/[0.05] dark:hover:bg-white/[0.08]">
              {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
              <span className="font-head text-[13px] tracking-wide">{blk.key}</span>
              <span className="rounded-full bg-[var(--primary)]/15 px-1.5 text-[10px] font-semibold text-[var(--primary)]">{blkCount}</span>
              {blkValue > 0 && <span className="ms-auto text-[11px] font-semibold tabular-nums opacity-80" data-testid={`aa-block-value-${blk.key}`}>{money(blkValue)}</span>}
            </button>
            {isOpen && (
              <div className="divide-y divide-black/5 dark:divide-white/5">
                {blk.items.map((item) => {
                  const key = `${blk.key}|${item}`;
                  const loaded = loadedFor(blk.key, item);
                  const count = itemsFor(blk.key, item).length;
                  const value = valueFor(blk.key, item);
                  const itemOpen = openItem === key;
                  return (
                    <div key={item} data-testid={`aa-item-${blk.key}-${slug(item)}`}>
                      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                        <button type="button" onClick={() => setOpenItem(itemOpen ? null : key)} data-testid={`aa-item-toggle-${blk.key}-${slug(item)}`} className="flex items-center gap-1 text-[12px] font-medium">
                          {itemOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}{item}
                        </button>
                        {count > 0 && <span className="rounded bg-black/[0.06] px-1.5 text-[10px] dark:bg-white/[0.08]" data-testid={`aa-item-count-${blk.key}-${slug(item)}`}>{count} rec</span>}
                        {value > 0 && <span className="text-[11px] font-semibold tabular-nums text-[var(--primary)]" data-testid={`aa-item-value-${blk.key}-${slug(item)}`}>{money(value)}</span>}
                        {loaded.length > 0 && <span className="ms-auto text-[9px] opacity-50">{loaded.map((s) => SEEDER_LABEL[s]).join(" · ")}</span>}
                      </div>
                      {itemOpen && (
                        <div className="border-t border-black/5 bg-black/[0.015] p-3 dark:border-white/5 dark:bg-white/[0.02]" data-testid={`aa-body-${blk.key}-${slug(item)}`}>
                          <SeederHost scopeCategory="Alternative Assets" block={blk.key} item={item} items={items} reload={reload}
                            loaded={loaded} onAdd={(s) => addSeeder(blk.key, item, s)} onRemove={(s) => removeSeeder(blk.key, item, s)}
                            tp={`aa-${blk.key}-${slug(item)}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
